import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ClearIcon from '@mui/icons-material/Clear';

interface EmptyStateProps {
  /** Mensagem quando há filtros aplicados (ex.: "Nenhuma licitação encontrada"). */
  title: string;
  /** Há filtros aplicados: mostra a ação de limpar; sem filtros, informa que ainda não há registros. */
  filtered?: boolean;
  onClearFilters?: () => void;
  /** Mensagem quando não há filtros e a listagem está vazia. */
  emptyTitle?: string;
}

/** Estado vazio das listagens: ícone, mensagem objetiva e ação para limpar os filtros. */
export default function EmptyState({ title, filtered = false, onClearFilters, emptyTitle = 'Ainda não há registros publicados' }: EmptyStateProps) {
  const showClear = filtered && !!onClearFilters;
  const Icon = filtered ? SearchOffIcon : InboxOutlinedIcon;

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.5,
        py: { xs: 6, md: 8 },
        px: 2,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'background.subtle',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <Icon sx={{ fontSize: '2rem' }} />
      </Box>
      <Typography variant="h4" component="p">
        {filtered ? title : emptyTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
        {filtered
          ? 'Nenhum resultado para os filtros escolhidos. Revise os termos ou limpe os filtros.'
          : 'Quando houver publicações, elas aparecerão aqui.'}
      </Typography>
      {showClear && (
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={onClearFilters} sx={{ mt: 1 }}>
          Limpar filtros
        </Button>
      )}
    </Box>
  );
}

/** Há filtro aplicado em um request de listagem (ignora paginação). */
export const hasActiveFilters = (request: object | undefined): boolean =>
  !!request &&
  Object.entries(request).some(
    ([key, value]) => !['page', 'pageSize'].includes(key) && value !== undefined && value !== null && value !== ''
  );
